// Union area of premium zones, in square miles.
//
// Overlapping zones are only charged once, so the advertiser's 30 mi² budget is
// measured on the union of the circles rather than the sum of π·r². Exact
// circle-union area needs arc integration; a fine grid gets us within ~0.1%,
// which is well inside the tolerance the server allows.
//
// The server is authoritative (Shapely, backend/server.py::_zones_union_area_sqmi).
// Keep the two in step.

const MI_PER_DEG_LAT = 69.05;
const MAX_CELLS = 300000;
const MIN_STEP_MI = 0.01;

const miPerDegLng = (lat) => 69.172 * Math.cos((lat * Math.PI) / 180);

const toLocal = (zones) => {
  const lat0 = zones[0].lat;
  const kx = miPerDegLng(lat0);
  return zones.map((z) => ({
    x: (Number(z.lng) - Number(zones[0].lng)) * kx,
    y: (Number(z.lat) - lat0) * MI_PER_DEG_LAT,
    r: Number(z.radius) || 0,
  }));
};

export const unionAreaSqMi = (rawZones) => {
  const zones = (rawZones || []).filter((z) => Number(z?.radius) > 0 && Number.isFinite(Number(z?.lat)) && Number.isFinite(Number(z?.lng)));
  if (zones.length === 0) return 0;
  if (zones.length === 1) return Math.PI * zones[0].radius * zones[0].radius;

  const pts = toLocal(zones);
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of pts) {
    minX = Math.min(minX, p.x - p.r); maxX = Math.max(maxX, p.x + p.r);
    minY = Math.min(minY, p.y - p.r); maxY = Math.max(maxY, p.y + p.r);
  }
  const w = maxX - minX, h = maxY - minY;
  const step = Math.max(MIN_STEP_MI, Math.sqrt((w * h) / MAX_CELLS));
  const cell = step * step;

  let area = 0;
  for (let y = minY + step / 2; y < maxY; y += step) {
    for (let x = minX + step / 2; x < maxX; x += step) {
      for (const p of pts) {
        const dx = x - p.x, dy = y - p.y;
        if (dx * dx + dy * dy <= p.r * p.r) { area += cell; break; }
      }
    }
  }
  return area;
};

// Largest radius for zones[index] (or a new zone appended) that keeps the union
// inside `budget`. Binary search, because union area isn't invertible.
export const maxRadiusWithinBudget = (zones, index, candidate, budget, hardCap = 10) => {
  const others = (zones || []).filter((_, i) => i !== index);
  const probe = (r) => unionAreaSqMi([...others, { ...candidate, radius: r }]);
  if (probe(hardCap) <= budget) return hardCap;
  let lo = 0, hi = hardCap;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (probe(mid) <= budget) lo = mid; else hi = mid;
  }
  return lo;
};
